import { NumberType_Enum } from '@weave/schema-gen-ts/dist/schemas/comm/phone-number/v1/phone_number_service.pb';
import { BulkMessagesQueries } from '@frontend/api-messaging';
import { BulkTextTemplateEditor, DEFAULT_BULK_SHORT_URL } from '@frontend/bulk-text-template-editor';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { phone, Stepper, TagType, Text } from '@frontend/design-system';
import { useBulkEmailEditorShallowStore } from '../../../hooks';
import { overrideTextStyles, stepValueContainerStyles } from '../styles';
import { Step, StepId } from '../types';

type UseEmailContentStepParams = {
  contentError: string;
  next: (stepId: StepId) => void;
  previous: (stepId: StepId) => void;
};

export const useTextContentStep = ({ contentError, next, previous }: UseEmailContentStepParams): Step => {
  const { t } = useTranslation('bulk-messaging');
  const store = useBulkEmailEditorShallowStore(
    'setSubject',
    'setTemplateHtml',
    'setTemplateJson',
    'templateHtml',
    'templateJson',
    'thumbnailImage',
    'thumbnailUrl'
  );

  const id: StepId = 'content';

  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();
  const locationId = selectedLocationIds[0] ?? '';
  const locationData = accessibleLocationData[locationId];

  const { data: phoneNumber } = BulkMessagesQueries.useDefaultPhoneRecord({
    locationId,
    numberType: NumberType_Enum.SMS,
  });

  const tags: TagType[] = [
    {
      label: t('First Name'),
      key: '{{FIRST_NAME}}',
      value: 'Jonathan',
    },
    {
      label: t('Last Name'),
      key: '{{LAST_NAME}}',
      value: 'Smith',
    },
    {
      label: t('Preferred Name'),
      key: '{{PREFERRED_NAME}}',
      value: 'Jon',
    },
    {
      label: t('Practice Name'),
      key: '{{PRACTICE_NAME}}',
      value: locationData?.name ?? '',
    },
    {
      label: t('Practice Phone'),
      key: '{{PRACTICE_PHONE}}',
      value: phone(phoneNumber ?? '', { hideCountry: true }),
    },
    {
      label: t('Online Bill Pay Link'),
      key: '{{ONLINE_BILL_PAY_LINK}}',
      value: DEFAULT_BULK_SHORT_URL,
    },
  ];

  return {
    id,
    Component: (
      <>
        <Stepper.Title>{t('Text Message Content')}</Stepper.Title>

        <Stepper.Content>
          <Text css={{ marginBottom: theme.spacing(2) }}>
            {t('Customize your message below. Use dynamic fields to personalize your message for each recipient.')}
          </Text>
          <BulkTextTemplateEditor
            error={contentError}
            initialTemplate={store.templateHtml}
            onBlur={() => {}}
            onChange={store.setTemplateHtml}
            readOnly={false}
            tags={tags}
            isNewTag
          />
        </Stepper.Content>

        <Stepper.ButtonBarAlternate>
          <Stepper.ButtonAlternate onClick={() => previous(id)} position='secondary' type='none'>
            {t('Previous')}
          </Stepper.ButtonAlternate>
          <Stepper.ButtonAlternate onClick={() => next(id)} position='primary' type='none'>
            {t('Next')}
          </Stepper.ButtonAlternate>
        </Stepper.ButtonBarAlternate>
      </>
    ),
    collapsedValue: (
      <div css={{ ...stepValueContainerStyles, flexDirection: 'column' }}>
        <Text>{t('Text Message Content')}</Text>
        <Text css={{ ...overrideTextStyles, textTransform: 'initial' }}>{store.templateHtml}</Text>
      </div>
    ),
  };
};
